import { useCallback } from "react";
import { usePreferences } from "@/providers/PreferencesProvider";
import { en } from "@/locales/en";
import { id } from "@/locales/id";

type Primitive = string | number;
type Params = Record<string, Primitive>;

const messages = { id, en } as const;

function resolveKey(source: unknown, key: string | null | undefined): unknown {
    if (typeof key !== "string" || key.length === 0) return undefined;
    return key.split(".").reduce<unknown>((acc, part) => {
        if (!acc || typeof acc !== "object") return undefined;
        return (acc as Record<string, unknown>)[part];
    }, source);
}

function applyParams(text: string, params?: Params): string {
    if (!params) return text;
    return Object.entries(params).reduce((result, [key, value]) => {
        return result.replaceAll(`{${key}}`, String(value));
    }, text);
}

/**
 * Same contract as the Portal `useI18n`: `t("nav.plan.label", { count: 3 })`.
 * Falls back to English, then to the raw key.
 */
export function useI18n() {
    const { language } = usePreferences();
    const locale = language === "en" ? "en-US" : "id-ID";

    const t = useCallback(
        (key: string, params?: Params): string => {
            if (!key) return "";

            const value = resolveKey(messages[language], key);
            if (typeof value === "string") {
                return applyParams(value, params);
            }

            const fallback = resolveKey(messages.en, key);
            if (typeof fallback === "string") {
                return applyParams(fallback, params);
            }

            return key;
        },
        [language],
    );

    return { language, locale, t };
}
